/**
 * SP3 intelligence-surface feature flags.
 *
 * One flag per page cluster (see plans/architecture/2026-07-03-sp3-admin-ui-port-plan.md).
 * Default off everywhere; environments enable via the runtime ConfigMap
 * (window.__RUNTIME_CONFIG__) or a build-time VITE_ env. Register P24:
 * dev-only first; VITE_ENABLE_RUNTIME_INGESTION additionally REPLACES the
 * legacy capsule wizard wherever it is on, so it must not be enabled in an
 * environment until the ingestion-draft endpoints are proxied there
 * (plans/architecture/sp3-wiring-table.md).
 */
import { getRuntimeEnv } from './runtime-env';

export const intelligenceFeatures = {
  /** RuntimeIngestionPage at /intelligence/capsules/new (replaces legacy wizard when on). */
  runtimeIngestion: () => getRuntimeEnv('VITE_ENABLE_RUNTIME_INGESTION') === 'true',
  /** RetrievalPage + LensCloud at /intelligence/retrieval. */
  retrievalTuning: () => getRuntimeEnv('VITE_ENABLE_RETRIEVAL_TUNING') === 'true',
  /** FineTuningPage at /intelligence/fine-tuning and the extraction curation flows. */
  extractionCuration: () => getRuntimeEnv('VITE_ENABLE_EXTRACTION_CURATION') === 'true',
  /**
   * Capsule-detail intelligence tabs (Ontology, Topology, Context, Enrichment).
   * On when ANY of the three SP3 flags is on — the tabs surface the runtime
   * contracts that all three page clusters write to, so any enabled cluster
   * makes them meaningful. Hidden tabs render nothing (register P8/P24).
   */
  capsuleTabs: () =>
    intelligenceFeatures.runtimeIngestion() ||
    intelligenceFeatures.retrievalTuning() ||
    intelligenceFeatures.extractionCuration(),
  /**
   * Explicit dev-only base-URL override pointing runtime clients directly at
   * an isolated Intelligence runtime. The normal local stack and every
   * deployed environment leave this empty and use the governance host proxy.
   */
  runtimeUrlOverride: () => getRuntimeEnv('VITE_INTELLIGENCE_RUNTIME_URL') ?? '',
  /**
   * SP2 cutover-safety: legacy Rust intelligence-zone surfaces (taxonomy,
   * overrides, enrichment status/progress, stream status/schema, feedback,
   * annotations, and event-spine) are permitted by DEFAULT. Capsule reads
   * additionally require authoritative per-host legacyZoneSurfaces evidence.
   * This flag cannot establish that a selected host supports the zone. It is
   * INVERSE-polarity: absent/empty/any-non-"false" value keeps the surfaces
   * visible; only the literal string "false" hides them. At cutover the hooks
   * behind those contracts stay dark instead of calling the retired zone.
   */
  legacyZoneSurfaces: () => getRuntimeEnv('VITE_ENABLE_LEGACY_ZONE_SURFACES') !== 'false',
  /**
   * BackfillPage at /intelligence/backfill: a one-time central-registry
   * migration tool for operators. Off unless explicitly enabled; slated for
   * removal once the migration is complete everywhere (Agion#1897).
   */
  backfill: () => getRuntimeEnv('VITE_ENABLE_INTELLIGENCE_BACKFILL') === 'true',
};
